import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { getValidatedAuthToken } from './auth0';
import { BadRequestError, ForbiddenError, NotFoundError, UnauthorisedError, ValidationError } from './errors';

export const fetcher = (baseURL: string) => ({
  async request<T>(url: string, method: Method, contentType: string, body?: any): Promise<T> {
    const headers: any = {};

    const token = await getValidatedAuthToken();

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const config: AxiosRequestConfig = {
      baseURL,
      method,
      url,
      headers,
      data: body,
      timeout: 10000,
      responseType: /pdf|octet-stream/.test(contentType) ? 'blob' : undefined,
    };

    try {
      const response = await axios(config);
      const { data, status } = response;

      if (status >= 400 && status < 600) {
        return this.handleError(response);
      }
      return data;
    } catch (error) {
      if (error.response) {
        return this.handleError(error.response);
      }

      if (error.request) {
        return this.handleError(error.request);
      }
    }
  },

  buildQueryString(query?: any): string {
    if (!query) {
      return '';
    }
    return Object.keys(query)
      .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(query[k])}`)
      .join('&');
  },

  handleError({ data, status, statusText, request }: AxiosResponse<any>): never {
    let error: Error;
    switch (status) {
      case 400:
        error = new BadRequestError(data.errors || statusText);
        break;
      case 401:
        error = new UnauthorisedError(statusText);
        break;
      case 403:
        error = new ForbiddenError(statusText);
        break;
      case 404:
        error = new NotFoundError(statusText);
        break;
      case 422:
        error = new ValidationError(data.errors, request.response);
        break;
      default: {
        error = new BadRequestError(data.errors);
        break;
      }
    }
    throw error;
  },
});
